
import { useState, FormEvent } from 'react';

export default function RequestTemplate() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    const form = e.currentTarget;
    const formData = new FormData(form);

    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const trade = formData.get('trade') as string;
    const templateType = formData.get('templateType') as string;
    const description = formData.get('description') as string;

    if (!name || !email || !trade || !templateType || !description) {
      setError('Please fill in all fields.');
      return;
    }

    if (description.length > 500) {
      setError('Description must be 500 characters or less.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('https://readdy.ai/api/form/d6apuk5t1k61917cmv0g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          name,
          email,
          trade,
          templateType,
          description,
        }).toString(),
      });

      if (response.ok) {
        setIsSuccess(true);
        form.reset();
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="request" className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 border border-amber-500/30 rounded-full mb-6">
              <i className="ri-mail-send-line text-amber-400" />
              <span className="text-amber-300 text-sm font-medium">Custom Request</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Can&apos;t Find What
              <span className="block text-amber-400">You Need?</span>
            </h2>

            <p className="text-lg text-slate-300 mb-8 leading-relaxed">
              Tell us what template or checklist would help your trade business. We create new resources based on community requests every month.
            </p>

            <div className="space-y-4">
              {[
                { icon: 'ri-time-line', text: 'New templates added monthly' },
                { icon: 'ri-team-line', text: 'Built with input from real tradies' },
                { icon: 'ri-gift-line', text: 'Free for all MtaPlannerPro users' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-amber-400`} />
                  </div>
                  <span className="text-slate-300">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
            {isSuccess ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-teal-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Request Submitted!</h3>
                <p className="text-slate-400">We&apos;ll review your request and notify you when the template is ready.</p>
              </div>
            ) : (
              <form
                id="request-template-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <h3 className="text-xl font-bold text-white mb-2">Request a Template</h3>
                <p className="text-sm text-slate-400 mb-6">Fill in the details and we&apos;ll create it for you.</p>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Your Name</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="e.g. John Smith"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="john@example.com"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 text-sm"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1.5">Your Trade</label>
                    <select
                      name="trade"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-amber-500 text-sm cursor-pointer appearance-none"
                      required
                      defaultValue=""
                    >
                      <option value="" disabled className="text-slate-900">Select trade</option>
                      <option value="Plumbing" className="text-slate-900">Plumbing</option>
                      <option value="Electrical" className="text-slate-900">Electrical</option>
                      <option value="HVAC" className="text-slate-900">HVAC</option>
                      <option value="Carpentry" className="text-slate-900">Carpentry</option>
                      <option value="Roofing" className="text-slate-900">Roofing</option>
                      <option value="Tiling" className="text-slate-900">Tiling</option>
                      <option value="Landscaping" className="text-slate-900">Landscaping</option>
                      <option value="Other" className="text-slate-900">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1.5">Type</label>
                    <select
                      name="templateType"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-amber-500 text-sm cursor-pointer appearance-none"
                      required
                      defaultValue=""
                    >
                      <option value="" disabled className="text-slate-900">Select type</option>
                      <option value="Template" className="text-slate-900">Template</option>
                      <option value="Checklist" className="text-slate-900">Checklist</option>
                      <option value="Guide" className="text-slate-900">Guide</option>
                      <option value="Spreadsheet" className="text-slate-900">Spreadsheet</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">
                    Describe what you need
                    <span className="text-slate-500 font-normal ml-1">(max 500 chars)</span>
                  </label>
                  <textarea
                    name="description"
                    rows={4}
                    maxLength={500}
                    placeholder="Describe the template or checklist you need..."
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 text-sm resize-none"
                    required
                  />
                </div>

                {error && (
                  <p className="text-red-400 text-sm bg-red-500/10 px-4 py-2 rounded-lg">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 rounded-xl font-bold hover:from-amber-500 hover:to-orange-600 transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-70 shadow-lg shadow-amber-500/25"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      Submit Request
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
